/**
 * Mid-tier NVR catalog — Uniview, Milesight, i-PRO (Panasonic), Avigilon.
 *
 * Every entry verified against the primary manufacturer datasheet (URL in
 * `datasheetUrl`). When a spec was not on the public datasheet, the field
 * is omitted rather than guessed.
 *
 * Sources:
 *  - Uniview NVR301-S3 / NVR301-S3-P / NVR302-E2-P / NVR304-E2-P /
 *    NVR308-X / NVR516 public PDF datasheets (uniview.com).
 *  - Milesight 4K H.265 Pro NVR 1000 / 5000 / 7000 / 8000 series datasheets
 *    (milesight.com).
 *  - i-PRO WJ-NX200 / WJ-NX300 / WJ-NX400 / WJ-NX510K spec sheets
 *    (i-pro.com).
 *  - Avigilon 4th Gen NVR (NVR4-VAL / NVR4X-STD / NVR4X-PRM) datasheet
 *    rev 7 + ACC ES 8-Port Appliance datasheet rev 11 (avigilon.com).
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const MID_TIER_NVR_CATALOG: EquipmentCatalogEntry[] = [
  // ─── Uniview ────────────────────────────────────────────────────────────
  // NVR301-S3 series (compact, single-disk, no built-in PoE).
  // Datasheet: NVR301-S3 Series V (Feb 2024), uniview.com.
  {
    id: 'uniview-nvr301-04s3',
    manufacturer: 'Uniview',
    model: 'NVR301-04S3',
    category: 'nvr-recorder',
    tagline: '4-ch compact NVR, 1 HDD bay, no PoE, 4K',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202405/11/20240511_1902295_%E3%80%90Datasheet%E3%80%91UNV%20NVR301-S3%20Series%20Network%20Video%20Recorder20240206%EF%BC%88Public%EF%BC%89_990920_168459_0.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 64,
      outputBandwidthMbps: 48,
      maxResolution: '4K',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100 RJ-45',
    },
  },
  {
    id: 'uniview-nvr301-08s3',
    manufacturer: 'Uniview',
    model: 'NVR301-08S3',
    category: 'nvr-recorder',
    tagline: '8-ch compact NVR, 1 HDD bay, no PoE, 4K',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202405/11/20240511_1902295_%E3%80%90Datasheet%E3%80%91UNV%20NVR301-S3%20Series%20Network%20Video%20Recorder20240206%EF%BC%88Public%EF%BC%89_990920_168459_0.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 64,
      outputBandwidthMbps: 48,
      maxResolution: '4K',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100 RJ-45',
    },
  },
  {
    id: 'uniview-nvr301-16s3',
    manufacturer: 'Uniview',
    model: 'NVR301-16S3',
    category: 'nvr-recorder',
    tagline: '16-ch compact NVR, 1 HDD bay, no PoE, 4K',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202405/11/20240511_1902295_%E3%80%90Datasheet%E3%80%91UNV%20NVR301-S3%20Series%20Network%20Video%20Recorder20240206%EF%BC%88Public%EF%BC%89_990920_168459_0.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 64,
      outputBandwidthMbps: 48,
      maxResolution: '4K',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // NVR301-S3-P series — single-disk + built-in PoE. Note: Uniview names
  // the PoE-bearing SKUs "-P4 / -P8" (not -E-P4 / -E-P8 as the original
  // request specified). The "-E-P" sub-line was the older B-series; the
  // current shipping product is NVR301-S3-P. We list both 4-port and
  // 8-port variants per the public datasheet.
  {
    id: 'uniview-nvr301-04s3-p4',
    manufacturer: 'Uniview',
    model: 'NVR301-04S3-P4',
    category: 'nvr-recorder',
    tagline: '4-ch NVR with 4 PoE ports (54 W total), 1 HDD, 4K',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202405/11/20240511_1902296_%E3%80%90Datasheet%E3%80%91UNV%20NVR301-S3-P%20Series%20Network%20Video%20Recorder20240206%EF%BC%88Public%EF%BC%89_990921_168459_0.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 64,
      outputBandwidthMbps: 48,
      maxResolution: '4K',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 4,
      poeBudgetW: 54,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100 RJ-45 uplink + 4× PoE',
    },
  },
  {
    id: 'uniview-nvr301-08s3-p8',
    manufacturer: 'Uniview',
    model: 'NVR301-08S3-P8',
    category: 'nvr-recorder',
    tagline: '8-ch NVR with 8 PoE ports (75 W total), 1 HDD, 4K',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202405/11/20240511_1902296_%E3%80%90Datasheet%E3%80%91UNV%20NVR301-S3-P%20Series%20Network%20Video%20Recorder20240206%EF%BC%88Public%EF%BC%89_990921_168459_0.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 64,
      outputBandwidthMbps: 48,
      maxResolution: '4K',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 8,
      poeBudgetW: 75,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100 RJ-45 uplink + 8× PoE',
    },
  },

  // NVR302-E2-P series — 2-bay + PoE.
  {
    id: 'uniview-nvr302-08e2-p8',
    manufacturer: 'Uniview',
    model: 'NVR302-08E2-P8',
    category: 'nvr-recorder',
    tagline: '8-ch NVR, 2 HDD bays, 8 PoE ports (130 W), 12 MP',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202204/29/20220429_1830763_UNV%20NVR302-E2-P%20Series%20V1.01-EN_923924_168459_0.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 8,
      poeBudgetW: 130,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100/1000 RJ-45 uplink + 8× PoE',
      alarmIO: '8 in / 2 out',
    },
  },
  {
    id: 'uniview-nvr302-16e2-p16',
    manufacturer: 'Uniview',
    model: 'NVR302-16E2-P16',
    category: 'nvr-recorder',
    tagline: '16-ch NVR, 2 HDD bays, 16 PoE ports (240 W), 12 MP',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202204/29/20220429_1830763_UNV%20NVR302-E2-P%20Series%20V1.01-EN_923924_168459_0.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 320,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 240,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100/1000 RJ-45 uplink + 16× PoE',
      alarmIO: '8 in / 2 out',
    },
  },

  // NVR304-E2-P series — 4-bay + PoE (RAID 1/5).
  {
    id: 'uniview-nvr304-16e2-p16',
    manufacturer: 'Uniview',
    model: 'NVR304-16E2-P16',
    category: 'nvr-recorder',
    tagline: '16-ch NVR, 4 HDD bays, 16 PoE ports (150 W), RAID 1/5',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202204/29/20220429_1830783_UNV%20NVR304-E2-P%20Series%20V1.01-EN_923933_168459_0.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 320,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 150,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['1', '5'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100/1000 RJ-45 uplink + 16× PoE',
      alarmIO: '16 in / 4 out',
    },
  },
  {
    id: 'uniview-nvr304-32e2-p16',
    manufacturer: 'Uniview',
    model: 'NVR304-32E2-P16',
    category: 'nvr-recorder',
    tagline: '32-ch NVR, 4 HDD bays, 16 PoE ports (150 W), RAID 1/5',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202204/29/20220429_1830783_UNV%20NVR304-E2-P%20Series%20V1.01-EN_923933_168459_0.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 320,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 150,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['1', '5'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100/1000 RJ-45 uplink + 16× PoE',
      alarmIO: '16 in / 4 out',
    },
  },

  // NVR308-X series — 8 HDD bays, no built-in PoE, 12 MP / RAID 0/1/5/6/10.
  // Public datasheet covers NVR308-16X / -32X / -64X (the modern revision
  // of the "-E-B" line the user asked about — feature parity per Uniview's
  // 2024 spec sheet).
  {
    id: 'uniview-nvr308-32x',
    manufacturer: 'Uniview',
    model: 'NVR308-32X',
    category: 'nvr-recorder',
    tagline: '32-ch NVR, 8 HDD bays, no PoE, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202405/11/20240511_1902276_%E3%80%90Datasheet%E3%80%91UNV%20NVR308-X%20Series%20Network%20Video%20Recorder20240206%EF%BC%88Public%EF%BC%89_990901_168459_0.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '12 MP',
      hddBays: 8,
      hddBayMaxTb: 12,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
      alarmIO: '16 in / 4 out',
    },
  },
  {
    id: 'uniview-nvr308-64x',
    manufacturer: 'Uniview',
    model: 'NVR308-64X',
    category: 'nvr-recorder',
    tagline: '64-ch NVR, 8 HDD bays, no PoE, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202405/11/20240511_1902276_%E3%80%90Datasheet%E3%80%91UNV%20NVR308-X%20Series%20Network%20Video%20Recorder20240206%EF%BC%88Public%EF%BC%89_990901_168459_0.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '12 MP',
      hddBays: 8,
      hddBayMaxTb: 12,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
      alarmIO: '16 in / 4 out',
    },
  },

  // NVR516 series — enterprise, 16 HDD bays, 3U, dual SFP uplinks.
  {
    id: 'uniview-nvr516-64',
    manufacturer: 'Uniview',
    model: 'NVR516-64',
    category: 'nvr-recorder',
    tagline: '64-ch enterprise NVR, 16 HDD bays, RAID 0/1/5/6/10/50/60',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202405/11/20240511_1902267_%E3%80%90Datasheet%E3%80%91UNV%20NVR516%20Series%20Network%20Video%20Recorder20240206%EF%BC%88Public%EF%BC%89_990892_168459_0.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '12 MP',
      hddBays: 16,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '3U',
      networkPorts: '4× 10/100/1000 RJ-45 + 2× 1G SFP',
      alarmIO: '24 in / 8 out',
    },
  },
  {
    id: 'uniview-nvr516-128',
    manufacturer: 'Uniview',
    model: 'NVR516-128',
    category: 'nvr-recorder',
    tagline: '128-ch enterprise NVR, 16 HDD bays, RAID 0/1/5/6/10/50/60',
    verified: true,
    datasheetUrl:
      'https://www.uniview.com/res/202405/11/20240511_1902267_%E3%80%90Datasheet%E3%80%91UNV%20NVR516%20Series%20Network%20Video%20Recorder20240206%EF%BC%88Public%EF%BC%89_990892_168459_0.pdf',
    specs: {
      channels: 128,
      inputBandwidthMbps: 512,
      outputBandwidthMbps: 384,
      maxResolution: '12 MP',
      hddBays: 16,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '3U',
      networkPorts: '4× 10/100/1000 RJ-45 + 2× 1G SFP',
      alarmIO: '24 in / 8 out',
    },
  },

  // ─── Milesight ──────────────────────────────────────────────────────────
  // Mini PoE NVR 1000 series (compact desktop, single HDD bay).
  {
    id: 'milesight-ms-n1004-upc',
    manufacturer: 'Milesight',
    model: 'MS-N1004-UPC',
    category: 'nvr-recorder',
    tagline: '4-ch mini PoE NVR, 4 PoE ports, 1 HDD bay',
    verified: true,
    datasheetUrl: 'https://www.milesight.com/security/product/poe-nvr-1000',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 40,
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 4,
      poeBudgetW: 40,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100/1000 RJ-45 uplink + 4× PoE',
    },
  },
  {
    id: 'milesight-ms-n1008-upc',
    manufacturer: 'Milesight',
    model: 'MS-N1008-UPC',
    category: 'nvr-recorder',
    tagline: '8-ch mini PoE NVR, 4 PoE ports, 1 HDD bay',
    verified: true,
    datasheetUrl: 'https://www.milesight.com/security/product/poe-nvr-1000',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 4,
      poeBudgetW: 40,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100/1000 RJ-45 uplink + 4× PoE',
    },
  },

  // 4K H.265 Pro NVR 5000 series — 1U rack, 2 HDD bays.
  {
    id: 'milesight-ms-n5008-uc',
    manufacturer: 'Milesight',
    model: 'MS-N5008-UC',
    category: 'nvr-recorder',
    tagline: '8-ch 4K NVR, 2 HDD bays, 1U, no PoE',
    verified: true,
    datasheetUrl:
      'https://www.milesight.com/static/file/en/download/datasheet/nvr/Milesight-4K-H.265-Pro-NVR-5000-Series-Datasheet-en.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
      alarmIO: '4 in / 1 out',
    },
  },
  {
    id: 'milesight-ms-n5008-upc',
    manufacturer: 'Milesight',
    model: 'MS-N5008-UPC',
    category: 'nvr-recorder',
    tagline: '8-ch 4K PoE NVR, 8 PoE ports (200 W), 2 HDD bays, 1U',
    verified: true,
    datasheetUrl: 'https://www.milesight.com/security/product/poe-nvr-5000',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 50,
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 8,
      poeBudgetW: 200,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45 uplink + 8× PoE',
    },
  },
  {
    id: 'milesight-ms-n5016-upc',
    manufacturer: 'Milesight',
    model: 'MS-N5016-UPC',
    category: 'nvr-recorder',
    tagline: '16-ch 4K PoE NVR, 16 PoE ports (200 W), 2 HDD bays, 1U',
    verified: true,
    datasheetUrl: 'https://www.milesight.com/security/product/poe-nvr-5000',
    specs: {
      channels: 16,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 50,
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45 uplink + 16× PoE',
    },
  },

  // 4K H.265 Pro NVR 7000 series — 1.5U rack, 4 HDD bays, RAID.
  {
    id: 'milesight-ms-n7016-uh',
    manufacturer: 'Milesight',
    model: 'MS-N7016-UH',
    category: 'nvr-recorder',
    tagline: '16-ch 4K NVR, 4 HDD bays, RAID 0/1/5/6/10, 1.5U',
    verified: true,
    datasheetUrl:
      'https://www.milesight.com/static/file/en/download/datasheet/nvr/Milesight-4K-H.265-Pro-NVR-7000-Series-Datasheet-en.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      networkPorts: '2× 10/100/1000 RJ-45',
      alarmIO: '16 in / 4 out',
    },
  },
  {
    id: 'milesight-ms-n7032-uh',
    manufacturer: 'Milesight',
    model: 'MS-N7032-UH',
    category: 'nvr-recorder',
    tagline: '32-ch 4K NVR, 4 HDD bays, RAID 0/1/5/6/10, 1.5U',
    verified: true,
    datasheetUrl:
      'https://www.milesight.com/static/file/en/download/datasheet/nvr/Milesight-4K-H.265-Pro-NVR-7000-Series-Datasheet-en.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      networkPorts: '2× 10/100/1000 RJ-45',
      alarmIO: '16 in / 4 out',
    },
  },

  // 4K H.265 Pro NVR 8000 series — 2U rack, 8 HDD bays + eSATA, RAID.
  {
    id: 'milesight-ms-n8032-uh',
    manufacturer: 'Milesight',
    model: 'MS-N8032-UH',
    category: 'nvr-recorder',
    tagline: '32-ch 4K NVR, 8 HDD bays + eSATA, RAID 0/1/5/6/10, 2U',
    verified: true,
    datasheetUrl:
      'https://www.milesight.com/static/file/en/download/datasheet/nvr/Milesight-4K-H.265-Pro-NVR-8000-Series-Datasheet-en.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 8,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
      alarmIO: '16 in / 4 out',
    },
  },
  {
    id: 'milesight-ms-n8064-uh',
    manufacturer: 'Milesight',
    model: 'MS-N8064-UH',
    category: 'nvr-recorder',
    tagline: '64-ch 4K NVR, 8 HDD bays + eSATA, RAID 0/1/5/6/10, 2U',
    verified: true,
    datasheetUrl:
      'https://www.milesight.com/static/file/en/download/datasheet/nvr/Milesight-4K-H.265-Pro-NVR-8000-Series-Datasheet-en.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 8,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
      alarmIO: '16 in / 4 out',
    },
  },

  // ─── i-PRO (Panasonic) ──────────────────────────────────────────────────
  // WJ-NX200K/G — 9 default / 32 max channels, 2 HDD bays, RAID 1.
  {
    id: 'ipro-wj-nx200kg',
    manufacturer: 'i-PRO',
    model: 'WJ-NX200K/G',
    category: 'nvr-recorder',
    tagline: '9-ch (32 max) NVR, 2 HDD bays, RAID 1, 192 Mbps rec',
    verified: true,
    datasheetUrl:
      'https://i-pro.com/products_and_solutions/sites/default/files/2023-01/WJ-NX200_2A-192DA_1658276914.9417.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 192,
      outputBandwidthMbps: 192,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 12,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['1'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
      alarmIO: '9 in / 1 out',
    },
  },

  // WJ-NX300K/G — 16 default / 32 max channels, 4 HDD bays, RAID 1/5/6.
  // 256 Mbps recording bandwidth, 512 Mbps total throughput.
  {
    id: 'ipro-wj-nx300kg',
    manufacturer: 'i-PRO',
    model: 'WJ-NX300K/G',
    category: 'nvr-recorder',
    tagline: '16-ch (32 max) NVR, 4 HDD bays, RAID 1/5/6, 256 Mbps rec',
    verified: true,
    datasheetUrl:
      'https://i-pro.com/products_and_solutions/en/surveillance/products/wj-nx300',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['1', '5', '6'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // WJ-NX400K/G — 64 default / 128 max channels, 9 HDD bays, RAID 1/5/6,
  // 384 Mbps recording bandwidth, 640 Mbps total.
  {
    id: 'ipro-wj-nx400kg',
    manufacturer: 'i-PRO',
    model: 'WJ-NX400K/G',
    category: 'nvr-recorder',
    tagline: '64-ch (128 max) enterprise NVR, 9 HDD bays, RAID 1/5/6',
    verified: true,
    datasheetUrl:
      'https://i-pro.com/products_and_solutions/sites/default/files/2023-12/WJ-NX400%20_%20i-PRO%20Global_R2_231206.pdf',
    specs: {
      channels: 128,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 640,
      maxResolution: '12 MP',
      hddBays: 9,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['1', '5', '6'],
      onvif: true,
      formFactor: '3U',
      networkPorts: '2× 10/100/1000 RJ-45 + 1× 10/100 maintenance',
      alarmIO: '32 in / 4 out',
    },
  },

  // WJ-NX510K — 64 default / 128 max channels, 9 HDD bays + 5 extension
  // units (max 972 TB), RAID 1/5/6, NDAA-compliant Intel CPU, FIPS 140-2
  // L3 secure element.
  {
    id: 'ipro-wj-nx510k',
    manufacturer: 'i-PRO',
    model: 'WJ-NX510K',
    category: 'nvr-recorder',
    tagline: '64-ch (128 max) high-security NVR, 9 HDD bays, NDAA, FIPS L3',
    verified: true,
    datasheetUrl:
      'https://i-pro.com/products_and_solutions/sites/default/files/2024-06/WJ-NX510K%20_%20i-PRO%20Global_R8_240603.pdf',
    specs: {
      channels: 128,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 640,
      maxResolution: '12 MP',
      hddBays: 9,
      hddBayMaxTb: 18,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['1', '5', '6'],
      onvif: true,
      formFactor: '3U',
      networkPorts: '2× 10/100/1000 RJ-45 + 1× 10/100 maintenance',
      alarmIO: '32 in / 4 out',
    },
  },

  // ─── Avigilon ───────────────────────────────────────────────────────────
  // 4th-gen NVR4 line. Channel counts are not listed as a hard cap on the
  // public datasheet (capacity is bandwidth-bound under ACC). We expose the
  // datasheet's published recording-bandwidth ceilings instead.
  {
    id: 'avigilon-nvr4-val',
    manufacturer: 'Avigilon',
    model: 'NVR4 Value (HD-NVR4-VAL)',
    category: 'nvr-recorder',
    tagline: '1U NVR, up to 4 HDD bays, RAID 5, 300 Mbps, ACC pre-loaded',
    verified: true,
    datasheetUrl:
      'https://www.avigilon.com/fs/documents/avigilon-nvr4-line-datasheet-en-rev7.pdf',
    specs: {
      // Avigilon datasheet states "site support for hundreds of cameras"
      // under ACC; we treat 300 Mbps as the binding capacity at the box.
      channels: 64,
      inputBandwidthMbps: 300,
      outputBandwidthMbps: 128,
      hddBays: 4,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['5'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '2× 1 GbE RJ-45 (+ optional 2× 10 GbE SFP+)',
    },
  },
  {
    id: 'avigilon-nvr4x-std',
    manufacturer: 'Avigilon',
    model: 'HD NVR4X Standard',
    category: 'nvr-recorder',
    tagline: '2U NVR, 8 HDD bays, RAID 6, 700 Mbps, ACC + Windows',
    verified: true,
    datasheetUrl:
      'https://www.avigilon.com/fs/documents/avigilon-nvr4-line-datasheet-en-rev7.pdf',
    specs: {
      channels: 128,
      inputBandwidthMbps: 700,
      outputBandwidthMbps: 450,
      hddBays: 8,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['6'],
      onvif: true,
      formFactor: '2U',
      networkPorts:
        '4× 1 GbE RJ-45 (+ optional 2× 10 GbE SFP+); OS on dual M.2 SSD RAID 1',
    },
  },
  {
    id: 'avigilon-nvr4x-prm',
    manufacturer: 'Avigilon',
    model: 'HD NVR4X Premium',
    category: 'nvr-recorder',
    tagline: '2U enterprise NVR, 12 HDD bays, RAID 6, 1500 Mbps',
    verified: true,
    datasheetUrl:
      'https://www.avigilon.com/fs/documents/avigilon-nvr4-line-datasheet-en-rev7.pdf',
    specs: {
      channels: 200,
      inputBandwidthMbps: 1500,
      outputBandwidthMbps: 600,
      hddBays: 12,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['6'],
      onvif: true,
      formFactor: '2U',
      networkPorts:
        '6× 1 GbE RJ-45 + 2× 10 GbE SFP+; dual redundant hot-swap PSU; OS on M.2 SSD RAID 1',
    },
  },
  {
    id: 'avigilon-nvr4x-prm-high-density',
    manufacturer: 'Avigilon',
    model: 'HD NVR4X Premium High-Density',
    category: 'nvr-recorder',
    tagline: '2U enterprise NVR, 18 HDD bays, RAID 60, 1500 Mbps',
    verified: true,
    datasheetUrl:
      'https://www.avigilon.com/fs/documents/avigilon-nvr4-line-datasheet-en-rev7.pdf',
    specs: {
      channels: 200,
      inputBandwidthMbps: 1500,
      outputBandwidthMbps: 600,
      hddBays: 18,
      codecs: ['H.265', 'H.264'],
      raidLevels: ['6'],
      onvif: true,
      formFactor: '2U',
      networkPorts:
        '4× 1 GbE RJ-45 + 2× 10 GbE SFP+; dual redundant hot-swap PSU; OS on M.2 SSD RAID 1',
    },
  },

  // Avigilon ACC ES 8-Port Appliance — desktop appliance with built-in
  // 8-port PoE+ switch (120 W budget), 1 HDD bay (4 or 8 TB), Linux.
  {
    id: 'avigilon-acc-es-8p',
    manufacturer: 'Avigilon',
    model: 'ACC ES 8-Port Appliance (VMA-ENVR1-8P)',
    category: 'nvr-recorder',
    tagline: '8-port PoE+ appliance, 1 HDD, 130 Mbps, Linux-embedded',
    verified: true,
    datasheetUrl:
      'https://www.avigilon.com/fs/documents/avigilon-acc-es-8port-appliance-datasheet-en-rev11.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 130,
      outputBandwidthMbps: 80,
      hddBays: 1,
      hddBayMaxTb: 8,
      poeInPorts: 8,
      poeBudgetW: 120,
      codecs: ['H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '2× 1 GbE uplink + 8× 10/100 PoE+ (802.3at)',
      alarmIO: '4 in / 4 out',
    },
  },
];
